/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef } from 'react';
import { Button as ButtonCore, type ButtonProps as ButtonCoreProps, type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { SocialsGoogleColorFillIcon } from '../../icons/socials-google-color-fill/socials-google-color-fill.icon';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';
import { Text, TextContext } from '../text/text.component';

interface GoogleButtonProps extends Omit<ButtonCoreProps, 'style' | 'className'> {
  /** The variant of the button. (Default: alternative) */
  variant?: keyof typeof variantVariants;
  /** The size of the button. (Default: 40) */
  size?: keyof typeof sizeVariants;
  /** Whether the button is rounded. (Default: false) */
  isRounded?: boolean;
  style?: stylex.StyleXStyles;
}

const GoogleButtonContext = createContext<ContextValue<GoogleButtonProps, HTMLButtonElement>>({});

const GoogleButton = forwardRef((props: GoogleButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, GoogleButtonContext);

  const { style, size = 40, isRounded = false, variant = 'alternative', children = 'Create account with Google', ...coreProps } = props;

  return (
    <ButtonCore
      ref={ref}
      {...stylex.props(
        styles.base,
        sizeVariants[size],
        variantVariants[variant],
        isRounded && styles.rounded,
        isRounded && roundedSizeVariants[size],
        style)}
      {...coreProps}>
      {(renderProps) => {
        renderProps.defaultChildren = typeof children === 'function' ? children?.(renderProps) : children;

        const isTextOnly = Children.toArray(renderProps.defaultChildren).every(c => typeof c === 'string');

        renderProps.defaultChildren = isTextOnly ? <Text>{renderProps.defaultChildren}</Text> : renderProps.defaultChildren;

        const iconSize = isRounded ? (size === 52 ? 38 : 32) : 28;

        return (
          <Provider
            values={[
              [TextContext, {
                color: (
                  ((variant === 'alternative' && renderProps.isPressed) && 'tertiary') ||
                  (variant === 'alternative' && 'secondary') ||
                  'primary'
                ),
                size: size === 52 ? 16 : 14,
                style: textStyles.base(iconSize),
              }],
              [IconContext, {
                size: iconSize,
                style: [iconSizeVariants[size], isRounded && variant === 'secondary' && iconStyles.floating],
              }]
            ]}>
            <SocialsGoogleColorFillIcon />
            {renderProps.defaultChildren}
          </Provider>
        );
      }}
    </ButtonCore>
  );
});

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderRadius: 14,
    borderStyle: 'none',
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'auto',
    },
    display: 'inline-flex',
    transition: 'background-color 0.2s ease-in-out',
  },
  rounded: {
    borderRadius: 999,
  },
});

const variantVariants = stylex.create({
  alternative: {
    backgroundColor: $.surface00,
    boxShadow: $.shadowCenter040Medium,
  },
  secondary: {
    backgroundColor: {
      default: $.buttonSurfaceSecondary,
      ':is([data-hovered], [data-pressed])': $.buttonSurfaceSecondaryHover,
    },
  }
});

const sizeVariants = stylex.create({
  52: {
    gap: 12,
    height: 52,
    paddingBlock: 4,
    paddingLeft: 12,
    paddingRight: 24,
  },
  40: {
    gap: 6,
    height: 40,
    paddingBlock: 4,
    paddingLeft: 10,
    paddingRight: 18,
  },
});

const roundedSizeVariants = stylex.create({
  52: {
    paddingLeft: 6,
    paddingRight: 24,
  },
  40: {
    gap: 8,
    paddingLeft: 3,
    paddingRight: 18,
  },
});

const iconStyles = stylex.create({
  floating: {
    backgroundColor: $.surface00,
    borderRadius: 999,
    boxShadow: $.shadowCenter040Medium,
    padding: 5,
  }
});

const iconSizeVariants = stylex.create({
  52: { padding: 5 },
  40: { padding: 3 },
});

const textStyles = stylex.create({
  base: (marginRight: number) => ({
    marginRight: marginRight - 8,
    width: '100%',
  }),
});

export { GoogleButton, GoogleButtonContext, type GoogleButtonProps };
